import {
    Link,
    useLocation,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import { useQueryClient, useIsMutating } from "@tanstack/react-query";
import React from "react";
import { ContentState } from "draft-js";
import {
    Dropdown,
    DropdownTrigger,
    DropdownContent,
    DropdownItem,
    Button,
    Dialog,
    DialogContent,
    FilterButton,
} from "@jhool-io/fe-components";

import { useFetchTasks } from "../../../../hooks/queries/tasks";
import { TaskStatus } from "../../../../utils/types/tasks";
import { ViewEditorText } from "../../../../components/TextEditor/ViewEditorText/ViewEditorText";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
} from "../../../../utils/helpers";
import { AddTextEditor } from "../../../../components/TextEditor/AddTextEditor/AddTextEditor";
import ListState from "../../../../components/ListState/ListState";
import {
    useUpdateTask,
    useDeleteTasks,
} from "../../../../hooks/mutations/tasks";
import useToast from "../../../../hooks/useToast";
import { UserPermisions } from "../../../../utils/types/user";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import ReassignTask from "../ReassignTask/ReassignTask";
import {
    getStatusTag,
    getTaskUsersToDisplay,
    userIsTasKViewer,
} from "../../helpers/tasks.helper";
import { useAppSelector } from "../../../../hooks/useRedux";
import Loader from "../../../../shared-ui/Loader/Loader";
import CalendarIcon from "../../../../icons/Calendar";
import UserIcon from "../../../../icons/User";
import Users from "../../../../icons/Users";
import EditIcon from "../../../../icons/Edit";

interface TaskDetailsProps {
    setHideModal?: () => void;
}

// Dialog for actions on Task Details modal
type ModalContent = "delete-task" | "reassign-task" | "forward-task";

export default function TaskDetails({ setHideModal }: TaskDetailsProps) {
    const [searchParams] = useSearchParams();
    // States for Editor
    const [getCurrentContent, setGetCurrentContent] =
        React.useState<ContentState>();
    const [showEditor, setShowEditor] = React.useState(false);
    const [isTaskDescriptionProvided, setIsTaskDescriptionProvided] =
        React.useState(true);
    const [dialogInView, setDialogInView] = React.useState<ModalContent | null>(
        null
    );

    const loggedInUser = useFetchUserDetails();

    const { practice } = useAppSelector((state) => state.userPractice);

    const { data, error, isLoading, isSuccess } = useFetchTasks({
        status: [
            TaskStatus.COMPLETED,
            TaskStatus.IN_PROGRESS,
            TaskStatus.NOT_STARTED,
        ],
        todo_id: searchParams.get("task_id") || "",
    });

    const assigneesDisplay = getTaskUsersToDisplay(
        loggedInUser?.data?.user_id as string,
        data?.data[0]?.assignees
    );

    const viewersDisplay = getTaskUsersToDisplay(
        loggedInUser?.data?.user_id as string,
        data?.data[0]?.viewers
    );

    // state to set the defaultValue in editor to task description
    const [taskDescription, setTaskDescription] = React.useState(
        data?.data[0]?.description
    );

    const toggleEditor = () => {
        setShowEditor(!showEditor);
    };

    const update = useUpdateTask();

    const deleteTasks = useDeleteTasks();

    const queryClient = useQueryClient();

    const { toast } = useToast();

    const location = useLocation();

    const navigate = useNavigate();

    // Check if any of the mutations are occuring
    const isUpdatingTodo = useIsMutating(["update-todo"]);
    const isDeletingTodo = useIsMutating(["delete-tasks"]);

    const completeTask = () => {
        const payload = {
            todos_to_update: [
                {
                    todo_id: data?.data[0].todo_id as string,
                    status: TaskStatus.COMPLETED,
                    title: data?.data[0].title as string,
                    description: data?.data[0]?.description as string,
                },
            ],
        };

        update.mutate(payload, {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ["todos"],
                });
                toast({
                    mode: "success",
                    message: "Task completed successfully!",
                });
                navigate(
                    searchParams.get("tab")
                        ? `${location.pathname}?tab=${searchParams.get("tab")}`
                        : location.pathname
                );
                setHideModal?.();
            },

            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not mark task as complete",
                });
            },
        });
    };

    const editTask = () => {
        if (!getCurrentContent?.hasText()) {
            // Additional info is empty
            setIsTaskDescriptionProvided(false);
            return;
        }

        setIsTaskDescriptionProvided(true);

        const payload = {
            todos_to_update: [
                {
                    todo_id: data?.data[0].todo_id as string,
                    status: data?.data[0].status as TaskStatus,
                    title: data?.data[0].title as string,
                    description: taskDescription as string,
                },
            ],
        };

        update.mutate(payload, {
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: ["todos"],
                });
                toast({
                    mode: "success",
                    message: "Task updated successfully!",
                });
                setShowEditor?.(false);
            },

            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update the task",
                });
            },
        });
    };

    const handleDeleteTasks = () => {
        deleteTasks.mutate([data?.data[0].todo_id as string], {
            onSuccess() {
                queryClient.invalidateQueries({
                    queryKey: [`todos`],
                });
                toast({
                    mode: "success",
                    message: "Tasks deleted successfully",
                });
                navigate(
                    searchParams.get("tab")
                        ? `${location.pathname}?tab=${searchParams.get("tab")}`
                        : location.pathname
                );
                setDialogInView(null);
                setHideModal?.();
            },
            onError(err) {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not delete tasks, please try again",
                });
            },
        });
    };

    const canDeleteTask = (id: string) => {
        if (practice?.permissions?.includes(UserPermisions.TODO_ALL_DELETE)) {
            return true;
        }
        if (loggedInUser.data?.user_id === id) {
            return true;
        }

        return false;
    };

    const getModalHeaderText = () => {
        switch (dialogInView) {
            case "delete-task":
                return "Delete task";

            case "forward-task":
                return "Forward task";

            case "reassign-task":
                return "Re-assign task";

            default:
                return "";
        }
    };

    const getModalOkText = () => {
        switch (dialogInView) {
            case "delete-task":
                return "Delete task";

            case "forward-task":
                return "Forward task";

            case "reassign-task":
                return "Assign task";

            default:
                return "";
        }
    };

    return (
        <>
            <Dialog open={Boolean(dialogInView)}>
                <DialogContent
                    title={getModalHeaderText()}
                    onSaveClick={() => {
                        if (dialogInView === "delete-task") {
                            handleDeleteTasks();
                        }
                    }}
                    onCancelClick={() => setDialogInView(null)}
                    handleCloseDialog={() => setDialogInView(null)}
                    cancelText="Cancel"
                    variant="center"
                    saveText={getModalOkText()}
                    showFooter
                    isDeleting={dialogInView === "delete-task"}
                    submitBtnFormValue="reassign-task"
                    isCancelBtnDisabled={
                        isUpdatingTodo > 0 || isDeletingTodo > 0
                    }
                    isSubmitBtnDisabled={
                        isUpdatingTodo > 0 || isDeletingTodo > 0
                    }
                >
                    <>
                        {dialogInView === "delete-task" && (
                            <div className="px-8">
                                <h3 className="text-xl font-medium mb-[8px]">
                                    Are you sure you want to delete this task?
                                </h3>
                                <p className="text-base">
                                    Are you sure you want to delete this task?
                                    Please note that this action cannot be
                                    undone.
                                </p>
                            </div>
                        )}
                        {(dialogInView === "reassign-task" ||
                            dialogInView === "forward-task") &&
                        data?.data[0] ? (
                            <ReassignTask
                                onFormSubmit={() => {
                                    setDialogInView(null);
                                    setHideModal?.();
                                }}
                                action={
                                    dialogInView === "forward-task"
                                        ? "forward"
                                        : "reassign"
                                }
                                task={data.data[0]}
                            />
                        ) : null}
                    </>
                </DialogContent>
            </Dialog>

            {isLoading && <Loader />}

            {error && error?.response?.status !== 404 && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error.response?.data.message ||
                        `Cannot display this task at this time please try again later`
                    }
                />
            )}

            {data && isSuccess && (
                <>
                    <div className="flex justify-between items-center border-b-[1px] [border-bottom:1px_solid_var(--color-stroke)]  pb-[32px]">
                        <div>
                            <h2 className="text-title font-bold mb-2">
                                {data.data[0]?.title &&
                                    makeStringFirstLetterCapital(
                                        data.data[0]?.title as string
                                    )}
                            </h2>

                            {data.data[0]?.status
                                ? getStatusTag(data.data[0]?.status)
                                : null}
                        </div>

                        {data.data[0]?.status !== TaskStatus.COMPLETED &&
                        !userIsTasKViewer(
                            data.data[0],
                            loggedInUser?.data?.user_id
                        ) ? (
                            <Dropdown>
                                <DropdownTrigger asChild>
                                    <FilterButton>Actions</FilterButton>
                                </DropdownTrigger>

                                <DropdownContent width={240} align="end">
                                    <DropdownItem
                                        onClick={() => completeTask()}
                                    >
                                        Mark as Completed
                                    </DropdownItem>

                                    <DropdownItem
                                        onClick={() =>
                                            setDialogInView("reassign-task")
                                        }
                                    >
                                        Re-assign
                                    </DropdownItem>

                                    <DropdownItem
                                        onClick={() =>
                                            setDialogInView("forward-task")
                                        }
                                    >
                                        Forward task
                                    </DropdownItem>

                                    {canDeleteTask(
                                        data.data[0]?.creator.user_id
                                    ) && (
                                        <DropdownItem
                                            onClick={() =>
                                                setDialogInView("delete-task")
                                            }
                                        >
                                            Delete Task
                                        </DropdownItem>
                                    )}
                                </DropdownContent>
                            </Dropdown>
                        ) : null}
                    </div>

                    <div className="[border-bottom:1px_solid_var(--color-stroke)] py-[32px]">
                        <div className="grid gap-y-8 gap-x-[--spacing-items] grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
                            <div className="flex flex-col">
                                <CalendarIcon className="size-5" />
                                <div className="flex flex-col gap-1 mt-3">
                                    <span className="text-caption font-medium text-textSec uppercase">
                                        Due Date
                                    </span>

                                    <p className="text-base">
                                        {formatDate(data.data[0]?.due_date)}
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-col">
                                <UserIcon className="size-5" />

                                <div className="flex flex-col gap-1 mt-3">
                                    <span className="text-caption font-medium text-textSec uppercase">
                                        Created by
                                    </span>
                                    <span className="text-base">
                                        {data.data[0]?.creator.user_id
                                            ? displayNameInRightFormat({
                                                  firstName:
                                                      data.data[0]?.creator
                                                          ?.first_name,
                                                  lastName:
                                                      data.data[0]?.creator
                                                          ?.last_name,
                                              })
                                            : "Mantle"}
                                    </span>
                                </div>
                            </div>
                            <div className="flex flex-col">
                                <UserIcon className="size-5" />

                                <div className="flex flex-col gap-1 mt-3">
                                    <span className="text-caption font-medium text-textSec uppercase">
                                        Client Name
                                    </span>

                                    {data.data[0]?.client &&
                                    data.data[0].client?.client_id ? (
                                        <Link
                                            className="text-base underline text-textLink"
                                            to={
                                                data.data[0]?.client?.client_id
                                                    ? `/clients/${data.data[0]?.client?.client_id}`
                                                    : ""
                                            }
                                        >
                                            {displayNameInRightFormat({
                                                firstName:
                                                    data?.data[0]?.client
                                                        ?.first_name,
                                                lastName:
                                                    data?.data[0]?.client
                                                        ?.last_name,
                                            })}
                                        </Link>
                                    ) : (
                                        "--"
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col">
                                <Users className="size-5" />

                                <div className="flex flex-col gap-1 mt-3">
                                    <span className="text-caption font-medium text-textSec uppercase">
                                        Assignees
                                    </span>

                                    <div className="text-base">
                                        {assigneesDisplay}
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col">
                                <Users className="size-5" />

                                <div className="flex flex-col gap-1 mt-3">
                                    <span className="text-caption font-medium text-textSec uppercase">
                                        Viewers
                                    </span>

                                    <div className="text-base">
                                        {viewersDisplay}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {data.data[0]?.description && (
                            <div className="flex gap-[--spacing-items] flex-wrap mt-8">
                                <div className="flex flex-col">
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className="shrink-0 size-5"
                                    >
                                        <line x1="8" y1="6" x2="21" y2="6" />
                                        <line x1="8" y1="12" x2="21" y2="12" />
                                        <line x1="8" y1="18" x2="21" y2="18" />
                                        <line x1="3" y1="6" x2="3.01" y2="6" />
                                        <line
                                            x1="3"
                                            y1="12"
                                            x2="3.01"
                                            y2="12"
                                        />
                                        <line
                                            x1="3"
                                            y1="18"
                                            x2="3.01"
                                            y2="18"
                                        />
                                    </svg>
                                    <div className="flex flex-col gap-1 mt-3">
                                        <h3 className="text-caption font-medium text-textSec uppercase">
                                            Description
                                        </h3>
                                        <div className="*:!text-base">
                                            <ViewEditorText
                                                text={data.data[0]?.description}
                                            />

                                            {loggedInUser.data?.user_id ===
                                                data.data[0]?.creator.user_id &&
                                            data.data[0]?.status !==
                                                TaskStatus.COMPLETED &&
                                            !userIsTasKViewer(
                                                data.data[0],
                                                loggedInUser.data?.user_id
                                            ) ? (
                                                <Button
                                                    type="button"
                                                    variant="can-hover"
                                                    size="auto"
                                                    aria-label="edit-btn"
                                                    onClick={() =>
                                                        toggleEditor()
                                                    }
                                                    className="mt-2"
                                                >
                                                    <EditIcon />
                                                </Button>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {showEditor && (
                        <div className="mt-[--spacing-items]">
                            <div className="fg ">
                                <AddTextEditor
                                    title="Write Task Description..."
                                    defaultValue={data?.data[0]?.description}
                                    onEditorTextChange={setTaskDescription}
                                    setIsRequiredFieldProvided={
                                        setGetCurrentContent
                                    }
                                    isRequiredFieldProvided={
                                        isTaskDescriptionProvided ||
                                        getCurrentContent?.hasText()
                                    }
                                />
                            </div>
                            <div className="justify-end flex">
                                <Button
                                    disabled={
                                        !isTaskDescriptionProvided ||
                                        update.isLoading
                                    }
                                    onClick={editTask}
                                >
                                    Save
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
}
